/**
 * Calendar API — polls Marinus's Outlook calendar for recent appointments.
 *
 * Detects financial planning appointments that have just ended and
 * triggers email prompts to submit the file in Inbox.
 */

import { graphRequest } from './graphClient';
import { formatLongDate } from '../utils/dateHelpers';

/**
 * Fetch recent calendar events that match financial planning appointments.
 *
 * Looks for events ending in the past 10 minutes that contain
 * "financial planning" in the title.
 *
 * @returns {Promise<Array>} matching calendar events
 */
export async function getRecentFinancialPlanningEvents() {
  const now = new Date();
  const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);

  // Query Outlook calendar for events ending in the past 10 minutes
  const startISO = tenMinutesAgo.toISOString();
  const endISO = now.toISOString();

  const url = `/me/calendarView?startDateTime=${startISO}&endDateTime=${endISO}&$select=id,subject,start,end,attendees&$orderby=end/dateTime desc`;

  try {
    const response = await graphRequest(url);
    const events = response.value || [];

    // Filter for financial planning events
    return events.filter((event) => {
      const subject = (event.subject || '').toLowerCase();
      return (
        subject.includes('personal financial planning') ||
        subject.includes('financial planning')
      );
    });
  } catch (err) {
    console.error('Calendar polling failed:', err);
    return [];
  }
}

/**
 * Parse a calendar event to extract client information.
 *
 * @param {object} event — a Graph API calendar event
 * @returns {object} { clientName, appointmentDate, eventId }
 */
export function parseCalendarEvent(event) {
  const subject = event.subject || '';
  const eventId = event.id;

  // Extract client name: take the last segment after " - "
  const parts = subject.split(' - ').map((p) => p.trim());
  let clientName = parts.length >= 2 ? parts[parts.length - 1] : '';

  // Clean up parenthetical content from the name
  clientName = clientName.replace(/\s*\(.*\)\s*$/, '').trim();

  // Extract appointment date from the event end time
  const appointmentDate = event.end?.dateTime
    ? new Date(event.end.dateTime).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  return {
    clientName,
    appointmentDate,
    eventId,
    originalSubject: subject,
  };
}

/**
 * Build a deep-link URL for the file submission form, pre-populated
 * with client data from the calendar event.
 *
 * @param {object} eventData — parsed calendar event data
 * @returns {string} URL with query parameters
 */
export function buildSubmitDeepLink(eventData) {
  const baseUrl = window.location.origin;
  const params = new URLSearchParams({
    clientName: eventData.clientName,
    appointmentDate: eventData.appointmentDate,
    eventId: eventData.eventId || '',
  });

  return `${baseUrl}/submit?${params.toString()}`;
}

/**
 * Build the email body for a post-consultation submission prompt.
 *
 * @param {object} eventData — parsed calendar event data
 * @returns {object} { subject, body } for sendEmail
 */
export function buildConsultationPromptEmail(eventData) {
  const deepLink = buildSubmitDeepLink(eventData);
  const dateStr = formatLongDate(eventData.appointmentDate);

  const subject = `Inbox: File submission for ${eventData.clientName} — ${dateStr}`;

  const body = `
    <div style="font-family: 'DM Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #1A2B3C;">
      <h2 style="color: #0F4C75; margin-bottom: 0.5rem;">File Submission Reminder</h2>
      <p>Hi Marinus,</p>
      <p>Your consultation with <strong>${eventData.clientName}</strong> has just ended.</p>
      <p>When you're ready, click the link below to submit the file and instructions to Wouter:</p>
      <p style="margin: 1.5rem 0;">
        <a href="${deepLink}" style="display: inline-block; padding: 12px 28px; background: #0F4C75; color: white; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 15px;">
          SUBMIT FILE FOR ${eventData.clientName.toUpperCase()} →
        </a>
      </p>
      <p style="color: #6B7A8D; font-size: 14px;">
        The file will be pre-populated with:<br />
        • Client: ${eventData.clientName}<br />
        • Consultation date: ${dateStr}
      </p>
      <p style="color: #6B7A8D; font-size: 14px;">
        You can action this when convenient — it will be waiting for you.
      </p>
      <hr style="border: none; border-top: 1px solid #D0DAE6; margin: 24px 0;" />
      <p style="color: #6B7A8D; font-size: 12px;">
        Inbox © 2025 — Private & Confidential
      </p>
    </div>
  `;

  return { subject, body };
}
